import React, { useState, useEffect, useCallback } from 'react';
import Button from '@salesforce/design-system-react/module/components/button';
import Dropdown from '@salesforce/design-system-react/module/components/menu-dropdown';
import DataTable from '@salesforce/design-system-react/module/components/data-table';
import DataTableColumn from '@salesforce/design-system-react/module/components/data-table/column';
import DataTableCell from '@salesforce/design-system-react/module/components/data-table/cell';
import Icon from '@salesforce/design-system-react/module/components/icon';
import PageHeader from '@salesforce/design-system-react/module/components/page-header';
import Spinner from '../../components/Spinner';
import { PageHeaderControl } from '@salesforce/design-system-react';
import Input from '@salesforce/design-system-react/components/input';
import InputIcon from '@salesforce/design-system-react/components/icon/input-icon';
import IconSettings from '@salesforce/design-system-react/components/icon-settings';
import debounce from 'lodash.debounce';
import {
  deleteExchangeAccount,
  ExchangeAccountsSelector,
  getExchangeAccounts,
  enableExchangeAccount,
  actions as exchangeActions
} from '../../store/exchanges/exchangeAccountSlice';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { getTimeIntervalText } from '../../utilities/datetime';
import { exchangePageOptions } from '../../constants/defiAccount';
import toast, { Toaster } from 'react-hot-toast';
import { isEmpty } from '@firebase/util';
import { getNumbersWithDecimalPlaces, getNumberWithCommas } from '../../utilities/money';
import ProviderLogo from '../../components/shared/ProviderLogo';
import AddExchange from '../../components/Modal/addExchange';
import { actions } from '../../components/Accounts/actions';
import ActionRemove from '../../components/Modal/actionRemove';
import { ACTION_TYPES } from '../../constants/types';
import { EXCHANGES } from '../../constants/exchange';
import { getCurrentUser } from './../../services/auth';
import ActionAccounts from '../../components/Modal/actionAccouts';
import { deleteAccount, getAccounts } from '../../store/accounts/slice';
import ActionExchanges from '../../components/Modal/actionExchanges';
import { getSyncTime } from '../../services/snapshot';
import Tooltip from '@salesforce/design-system-react/components/tooltip';
import ReactivateAccount from '../../components/Modal/reactivateAccount';

const CustomDataTableProvider = ({ children, ...props }) => {
  const { item } = props;

  return (
    <DataTableCell title={children} {...props}>
      {EXCHANGES[children] ? EXCHANGES[children] : children}
    </DataTableCell>
  );
};
CustomDataTableProvider.displayName = DataTableCell.displayName;

const CustomDataTableCategoryCell = ({ children, ...props }) => {
  const { item } = props;

  return (
    <DataTableCell title={children} {...props}>
      {children ? children : '--'}
    </DataTableCell>
  );
};
CustomDataTableCategoryCell.displayName = DataTableCell.displayName;

const CustomDataTableTypeCell = ({ children, ...props }) => {
  const { item } = props;

  return (
    <DataTableCell title={children} {...props}>
      {children ? children : '--'}
    </DataTableCell>
  );
};
CustomDataTableTypeCell.displayName = DataTableCell.displayName;

const CustomLastTransactionDate = ({ children, ...props }) => {
  const { item } = props;
  return (
    <DataTableCell title={children} {...props}>
      {item.updatedAt ? <p>{item.updatedAt && item.updatedAt}</p> : <p>--</p>}
    </DataTableCell>
  );
};
CustomLastTransactionDate.displayName = DataTableCell.displayName;

const CustomDataTableName = ({ children, ...props }) => {
  const { item } = props;

  return (
    <DataTableCell title={children} {...props}>
      <Link
        to={`/exchanges/${item._id}/overview`}
      >
        <span className="slds-m-right_xx-small">
          <ProviderLogo provider={item?.cefi_account_provider} color={item?.account_color} />
        </span>
        {children}
      </Link>
    </DataTableCell>
  );
};
CustomDataTableName.displayName = DataTableCell.displayName;

const CustomLastTransaction = ({ children, ...props }) => {
  const { item } = props;

  return (
    <DataTableCell title={item?.last_transaction?.text} {...props}>
      {item?.last_transaction?.text ? (
        <Link to={`/transaction/${item?.last_transaction?.tx_hash}`}>
          {item?.last_transaction?.text && (
            <span className="slds-m-right_xx-small">
              <img src="/assets/icons/menus/transactions.svg" alt=""></img>
            </span>
          )}
          {item?.last_transaction?.text}
        </Link>
      ) : (
        '--'
      )}
    </DataTableCell>
  );
};
CustomLastTransaction.displayName = DataTableCell.displayName;

/**
 * Custom Balance cell to display formatted date
 */
const CustomDataTableBalanceCell = ({ children, ...props }) => {
  const { item } = props;
  const { wallet_total } = item;
  const amount = getNumberWithCommas(getNumbersWithDecimalPlaces(wallet_total, 2));
  return (
    <DataTableCell {...props}> {amount !== undefined ? `$ ${amount}` : '--,--'}</DataTableCell>
  );
};

CustomDataTableBalanceCell.displayName = DataTableCell.displayName;

const CustomActionTableDate = ({ children, ...props }) => {
  const { item } = props;
  const pause = item.status === false ? ACTION_TYPES.RESUME : ACTION_TYPES.PAUSE; // status is isSyncPaused here

  return (
    <DataTableCell title={'Actions'} {...props}>
      <Button
        className="CustomButtonLarge2 action-pause-resume-button"
        key={pause}
        label={pause + ' sync'}
        responsive
        disabled={!props.syncTime ? false : true}
        variant="neutral"
        title="Supend and pause sync"
        onClick={() =>
          props.setActionAccountsModal({
            open: true,
            type: pause,
            name: item.name,
            id: item._id,
            isSyncPaused: item.status,
          })
        }
      />

      <Button
        className="CustomButtonLarge2 action-delete-button"
        key="Remove"
        label="Remove"
        responsive
        disabled={!props.syncTime ? false : true}
        variant="text-destructive"
        title="Remove account and delete data"
        onClick={() =>
          props.setActionRemoveModal({
            open: true,
            type: ACTION_TYPES.REMOVE,
            name: item.name,
            title: 'Exchange',
            id: item._id,
          })
        }
      />
    </DataTableCell>
  );
};
CustomActionTableDate.displayName = DataTableCell.displayName;

export default function ExchangeAccount() {
  const dispatch = useDispatch();
  const { exchangeAccounts, loading, pagination, errors, success, message, errorCode, conflictAccount } =
    useSelector(ExchangeAccountsSelector);
  const [sortColumn, setSortColumn] = useState('');
  const [sortColumnDirection, setSortColumnDirection] = useState('');
  const [items, setItems] = useState([]);
  const [openAddExchangeModal, setOpenAddExchangeModal] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [paginated, setPaginated] = useState(0);
  const navigate = useNavigate();
  const [APICalledTime, setAPICalledTime] = useState(new Date());
  const [text, setText] = useState('Recently updated');
  const [searchText, setSearchText] = useState('');
  const [loadingRemove, setLoadingRemove] = useState(false);
  const [actionRemoveModal, setActionRemoveModal] = useState({
    open: false,
    type: '',
  });
  const [enableAccountModal, setEnableAccountModal] = useState(false);

  const [actionAccountsModal, setActionAccountsModal] = useState({
    open: false,
    type: '',
  });
  const [rows, setRowData] = React.useState('');
  const [query, setQuery] = React.useState('');

  const user = getCurrentUser();
  const syncTime = getSyncTime();

  useEffect(() => {
    dispatch(getExchangeAccounts());
  }, [dispatch, syncTime]);

  useEffect(() => {
    setHasMore(true);
    if (exchangeAccounts.length > 0) {
      setItems(exchangeAccounts);
    }
    setAPICalledTime(new Date());
    const data = getTimeIntervalText(APICalledTime);
    setText(data);
  }, [exchangeAccounts]);

  useEffect(() => {
    const interval = setInterval(() => {
      const data = getTimeIntervalText(APICalledTime);
      setText(data);
    }, 20000);

    return () => clearInterval(interval);
  }, [APICalledTime]);

  const handleSort = (sortColumn, ...rest) => {
    setItems([]);
    const { property, sortDirection } = sortColumn;
    setSortColumn(property);
    setSortColumnDirection(sortDirection);
    setHasMore(true);
    setIsPageLoading(false);
    setPaginated(0);
    dispatch(getExchangeAccounts({ sort_column: property, sort_order: sortDirection }));

    // sorting will actually be handled from api
    // so dispatch sort action in useEffect
    // dispatch(sortAccounte({sortDirection, sortProperty}))
    // and the update the state with sorted accounts lists this will be done by above useEffect
  };

  const handleExchangeAdd = (el) => {
    setOpenAddExchangeModal(true);
  };

  const handleLoadMore = () => {
    if (!loading && !isPageLoading) {
      if (pagination?.hasNextPage) {
        setIsPageLoading(true);
        setPaginated(paginated + 1);
        setTimeout(() => {
          if (pagination.pages <= 1 || paginated >= pagination.pages || !pagination.hasNextPage) {
            setHasMore(false);
            setIsPageLoading(false);
            return;
          }

          if (pagination.currentPage >= pagination.pages) {
            setHasMore(false);
            setIsPageLoading(false);
            return;
          }
          let url = new URLSearchParams(pagination.nextPageUrl);

          dispatch(
            getExchangeAccounts({
              limit: url.get('limit'),
              offset: url.get('offset'),
              q: searchText,
              sort_column: sortColumn,
              sort_order: sortColumnDirection,
            })
          );
          setIsPageLoading(false);
        }, 1000);
      } else {
        setIsPageLoading(false);
        setHasMore(false);
      }
    }
  };

  const refreshTable = () => {
    setHasMore(true);
    setPaginated(0);
    setItems([]);
    if (searchText !== '') {
      searchHandler(searchText);
    } else {
      dispatch(getExchangeAccounts());
    }
  };

  // const searchHandler = (value) => {
  //   setHasMore(false);
  //   // setItems([]);
  //   // setPaginated(0);
  //   setQuery(value);
  //   const results = items.filter((row) => {
  //     const regex = new RegExp(value, "gi");
  //     return row.name.match(regex);
  //   });
  //   setRowData(results);

  // };

  const searchHandler = useCallback(
    debounce((values) => {
      setHasMore(true);
      setItems([]);
      setPaginated(0);
      dispatch(
        getExchangeAccounts({
          q: values,
        })
      );
    }, 1000),
    []
  );

  // const items = rows.length === 0 && !query ? items : rows;

  const onSearchText = (value) => {
    setSearchText(value);
    searchHandler(value);
  };

  const controls = () => (
    <React.Fragment>
      <PageHeaderControl>
        <IconSettings iconPath="/assets/icons">
          <div className="">
            <Input
              iconLeft={
                <InputIcon
                  assistiveText={{
                    icon: 'Search',
                  }}
                  name="search"
                  category="utility"
                />
              }
              id="search_text"
              placeholder="Search Exchange"
              value={searchText}
              onChange={(e) => onSearchText(e.target.value)}
              fixedTextRight={
                <Tooltip
                  id="search-tooltip"
                  align="top right"
                  content="Search works on the basis of name, provider, category and type"
                  position='overflowBoundaryElement'
                />
              }
            />
          </div>
        </IconSettings>
      </PageHeaderControl>
      <PageHeaderControl>
        <Button
          assistiveText={{ icon: 'Refresh' }}
          iconCategory="utility"
          iconName="refresh"
          iconVariant="border-filled"
          variant="icon"
          onClick={() => {
            refreshTable();
          }}
        />
      </PageHeaderControl>
    </React.Fragment>
  );

  useEffect(() => {
    if (success) {
      toast.success(message, {
        id: 'exchange-add-success',
        duration: 3000,
      });
    }

    if (!isEmpty(errors)) {
      toast.error(errors, {
        id: 'exchange-add-error',
        duration: 3000,
      });
    }
    if(enableAccountModal && success){
      setEnableAccountModal(false);
    }
  }, [success, errors, message]);

  useEffect(() => {
    if (+errorCode === 409) {
      setEnableAccountModal(true);
    }
  }, [errorCode]);

  const removeAccount = (id) => {
    // dispatch remove account API
    if (id) {
      setLoadingRemove(true);
      const accountDetails = {
        id: id,
      };
      dispatch(deleteExchangeAccount(accountDetails)).then((res) => {
        if (res?.payload?.status === 200) {
          // setShowToast(true);
          setActionRemoveModal({
            open: false,
            type: '',
          });
          dispatch(getExchangeAccounts());
          setLoadingRemove(false);
        }
      });
    }
  };

  const handleEnableAccountConfirm = (id) => {
    const dataForEnable = { id, data: { status: true, isDeleted: false } };
    dispatch(enableExchangeAccount(dataForEnable));
  }

  const handleEnableAccountCancel = () => {
    dispatch(exchangeActions.resetErrorCode());
    setEnableAccountModal(false);
  }

  return (
    <>
      {isPageLoading ? (
        <Spinner />
      ) : (
        <div className="pageContent">
          <Toaster position="top-right" reverseOrder={false} />
          <PageHeader
            icon={<Icon assistiveText={{ label: 'Accounts' }} category="standard" name="account" />}
            info={`${items && items.length} items • ${text}`}
            joined
            label="Exchanges"
            title="All Exchanges"
            nameSwitcherDropdown={
              <Dropdown
                assistiveText={{ icon: 'Name Switcher' }}
                buttonClassName="slds-button_icon-small"
                buttonVariant="icon"
                iconCategory="utility"
                iconName="down"
                onSelect={(e) => {
                  navigate('/exchanges/' + e.value);
                }}
                id="page-header-name-switcher-dropdown"
                options={exchangePageOptions}
              />
            }
            onRenderActions={() =>
              actions({
                onAddAction: handleExchangeAdd,
                showDropdown: false,
                btnLabel:
                  !syncTime &&
                  `${
                    (user.account_type = 'organization' && user.role === 'user')
                      ? ''
                      : 'Add Exchange'
                  }`,
              })
            }
            onRenderControls={controls}
            truncate
            variant="object-home"
          />

          <div
            style={{ height: `${exchangeAccounts.length === 0 ? '100%' : 'calc(100vh - 220px)'}` }}
          >
            <DataTable
              assistiveText={{
                actionsHeader: 'actions',
                columnSort: 'sort this column',
                columnSortedAscending: 'asc',
                columnSortedDescending: 'desc',
                selectAllRows: 'Select all rows',
                selectRow: 'Select this row',
              }}
              fixedHeader
              fixedLayout
              items={items}
              id="DataTableExample-FixedHeaders"
              onSort={handleSort}
              hasMore={hasMore}
              onLoadMore={handleLoadMore}
              noRowHover
            >
              <DataTableColumn
                isSorted={sortColumn === 'name'}
                label="Name"
                primaryColumn
                property="name"
                sortable
                sortDirection={sortColumnDirection.name}
                width="10rem"
              >
                <CustomDataTableName />
              </DataTableColumn>
              <DataTableColumn label="Balance" property="wallet_total">
                <CustomDataTableBalanceCell />
              </DataTableColumn>

              <DataTableColumn label="Provider" sortable property="cefi_account_provider">
                <CustomDataTableProvider />
              </DataTableColumn>

              <DataTableColumn label="Category" sortable property="category">
                <CustomDataTableCategoryCell />
              </DataTableColumn>

              <DataTableColumn label="Type" sortable property="type">
                <CustomDataTableTypeCell />
              </DataTableColumn>

              {/* <DataTableColumn label="Last Transaction" property="last_transaction" sortable>
                <CustomLastTransaction />
              </DataTableColumn>
              <DataTableColumn label="Last Transaction Date" sortable property="updatedAt">
                <CustomLastTransactionDate />
              </DataTableColumn> */}
              {/* @TODO */}
              {user.role !== 'user' && (
                <DataTableColumn label="Action" width="20rem">
                  <CustomActionTableDate
                    syncTime={syncTime}
                    setActionRemoveModal={setActionRemoveModal}
                    setActionAccountsModal={setActionAccountsModal}
                  />
                </DataTableColumn>
              )}
            </DataTable>
            {(exchangeAccounts.length === 0 || items.length === 0) && !hasMore && (
              <div className="nodatafound">
                <div>
                  <img
                    src="/assets/empty-state/empty-folder.png"
                    alt=""
                    style={{ height: '150px' }}
                  />
                  <p style={{ textAlign: 'center', fontWeight: '700', fontSize: '20px' }}>
                    Data not available
                  </p>
                </div>
              </div>
            )}
            <AddExchange
              items={items || []}
              openModal={openAddExchangeModal}
              closeModal={() => setOpenAddExchangeModal(false)}
            />

            <ActionRemove
              modelData={actionRemoveModal}
              closeModal={setActionRemoveModal}
              removeData={removeAccount}
              isLoading={loadingRemove}
            />

            <ActionExchanges modelData={actionAccountsModal} closeModal={setActionAccountsModal} />

            <ReactivateAccount
              account={conflictAccount}
              isOpen={enableAccountModal}
              toggleOpen={handleEnableAccountCancel}
              onConfirm={handleEnableAccountConfirm}
              isLoading={loading}
              type={'exchange'}
            />
          </div>
        </div>
      )}
    </>
  );
}
